import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { ShieldCheck } from "lucide-react";

// ============================================================================
// Visual 1 — Big 95% with hand-drawn circle (Card: Certainty)
// ============================================================================
const Visual95 = () =>
<div className="relative flex h-28 w-60 items-center justify-center">
    <svg
      className="absolute inset-0 h-full w-full text-white/10"
      viewBox="0 0 254 104"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">

      <path
        d="M112.891 97.7022C140.366 97.0802 171.004 94.6715 201.087 87.5116C210.43 85.2881 219.615 82.6412 228.284 78.2473C232.198 76.3179 235.905 73.9942 239.348 71.3124C241.85 69.2557 243.954 66.7571 245.555 63.9408C249.34 57.3235 248.281 50.5341 242.498 45.6109C239.033 42.7237 235.228 40.2703 231.169 38.3054C219.443 32.7209 207.141 28.4382 194.482 25.534C184.013 23.1927 173.358 21.7755 162.64 21.2989C161.376 21.3512 160.113 21.181 158.908 20.796C158.034 20.399 156.857 19.1682 156.962 18.4535C157.115 17.8927 157.381 17.3689 157.743 16.9139C158.104 16.4588 158.555 16.0821 159.067 15.8066C160.14 15.4683 161.274 15.3733 162.389 15.5286C179.805 15.3566 196.626 18.8373 212.998 24.462C220.978 27.2494 228.798 30.4747 236.423 34.1232C240.476 36.1159 244.202 38.7131 247.474 41.8258C254.342 48.2578 255.745 56.9397 251.841 65.4892C249.793 69.8582 246.736 73.6777 242.921 76.6327C236.224 82.0192 228.522 85.4602 220.502 88.2924C205.017 93.7847 188.964 96.9081 172.738 99.2109C153.442 101.949 133.993 103.478 114.506 103.79C91.1468 104.161 67.9334 102.97 45.1169 97.5831C36.0094 95.5616 27.2626 92.1655 19.1771 87.5116C13.839 84.5746 9.1557 80.5802 5.41318 75.7725C-0.54238 67.7259 -1.13794 59.1763 3.25594 50.2827C5.82447 45.3918 9.29572 41.0315 13.4863 37.4319C24.2989 27.5721 37.0438 20.9681 50.5431 15.7272C68.1451 8.8849 86.4883 5.1395 105.175 2.83669C129.045 0.0992292 153.151 0.134761 177.013 2.94256C197.672 5.23215 218.04 9.01724 237.588 16.3889C240.089 17.3418 242.498 18.5197 244.933 19.6446C246.627 20.4387 247.725 21.6695 246.997 23.615C246.455 25.1105 244.814 25.5605 242.63 24.5811C230.322 18.9961 217.233 16.1904 204.117 13.4376C188.761 10.3438 173.2 8.36665 157.558 7.52174C129.914 5.70776 102.154 8.06792 75.2124 14.5228C60.6177 17.8788 46.5758 23.2977 33.5102 30.6161C26.6595 34.3329 20.4123 39.0673 14.9818 44.658C12.9433 46.8071 11.1336 49.1622 9.58207 51.6855C4.87056 59.5336 5.61172 67.2494 11.9246 73.7608C15.2064 77.0494 18.8775 79.925 22.8564 82.3236C31.6176 87.7101 41.3848 90.5291 51.3902 92.5804C70.6068 96.5773 90.0219 97.7419 112.891 97.7022Z"
        fill="currentColor" />

    </svg>
    <span className="relative block text-5xl sm:text-6xl font-bold font-outfit bg-gradient-to-br from-[#FFDE59] to-[#FF914D] bg-clip-text text-transparent">
      95%
    </span>
  </div>;


// ============================================================================
// Visual 2 — Shield inside orbital rings (Card: Secure by commitment)
// ============================================================================
const VisualShield = () =>
<div className="relative mx-auto flex aspect-square size-28 sm:size-32 rounded-full border border-white/10 before:absolute before:-inset-2 before:rounded-full before:border before:border-white/5 after:absolute after:-inset-5 after:rounded-full after:border after:border-white/[0.03]">
    <div className="m-auto flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#FFDE59]/15 to-[#FF914D]/15 border border-[#FF914D]/30">
      <ShieldCheck className="h-7 w-7 text-[#FF914D]" strokeWidth={1.5} />
    </div>
  </div>;


// ============================================================================
// Visual 3 — Tiered commitment bars (Card: Commitments up to £250K)
// ============================================================================
const VisualTiers = () => {
  const tiers = [
  { label: "\xA310k", h: "h-6" },
  { label: "\xA325k", h: "h-10" },
  { label: "\xA350k", h: "h-14" },
  { label: "\xA3100k", h: "h-20" },
  { label: "\xA3250k", h: "h-28" }];


  return (
    <div className="relative flex h-32 w-full items-end justify-center gap-2 sm:gap-3 px-2">
      {tiers.map((t, i) => {
        const isMax = i === tiers.length - 1;
        return (
          <div key={t.label} className="flex flex-col items-center gap-2 flex-1 max-w-[40px]">
            <div
              className={`w-full rounded-t-md transition-all ${t.h} ${
              isMax ?
              "bg-gradient-to-t from-[#FF914D] to-[#FFDE59] shadow-lg shadow-[#FF914D]/30" :
              "bg-white/10"}`
              } />

            <span className={`text-[9px] sm:text-[10px] font-manrope tracking-wide ${isMax ? "text-[#FFDE59]" : "text-white/40"}`}>
              {t.label}
            </span>
          </div>);

      })}
    </div>);

};

// ============================================================================
// CARD SHELL (consistent dark-themed glassmorphic card)
// ============================================================================
const EliteCard = ({ children, className = "", ...props }) =>
<Card
  className={`relative overflow-hidden border-white/10 bg-gradient-to-b from-white/[0.04] to-white/[0.01] backdrop-blur-sm hover:border-[#FF914D]/25 transition-colors duration-300 ${className}`}
  {...props}>

    {/* Subtle top gradient shine */}
    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#FF914D]/30 to-transparent pointer-events-none" />
    {children}
  </Card>;


// ============================================================================
// MAIN FEATURES BENTO COMPONENT
// ============================================================================

export default function FeaturesBento() {
  return (
    <section
      id="secure"
      className="relative bg-black pt-2 pb-4 md:pt-4 md:pb-6"
      data-testid="features-bento-section">

      {/* Subtle glow backdrop */}
      <div className="absolute left-1/2 top-0 -translate-x-1/2 h-[400px] w-[600px] bg-gradient-to-b from-[#FF914D]/10 to-transparent blur-3xl pointer-events-none opacity-40" />

      <div className="relative mx-auto max-w-3xl lg:max-w-6xl px-6">
        {/* Section Heading */}
        <div className="mx-auto max-w-4xl text-center mb-8 md:mb-10">
          <h2 className="text-2xl sm:text-3xl lg:text-[40px] font-didot italic leading-[1.2]">
            <span className="text-white">The same certainty you trust. </span>
            <span className="bg-gradient-to-r from-[#FFDE59] to-[#FF914D] bg-clip-text text-transparent">Now with commitment levels that match the properties you sell.</span>
          </h2>
        </div>

        {/* Bento Grid: 3 cards, equal height across all breakpoints */}
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 items-stretch">

          {/* Card 1: Certainty */}
          <EliteCard
            className="h-full"
            data-testid="elite-card-certainty">

            <CardContent className="h-full flex flex-col items-center text-center pt-10 pb-8 px-6">
              <Visual95 />
              <h3 className="mt-6 text-2xl sm:text-[26px] font-didot italic text-white">
                Certainty
              </h3>
              <p className="mt-3 text-sm sm:text-[15px] text-white/65 font-manrope leading-relaxed">
                95% of sales secured through Gazeal Elite go all the way to completion, which means that for every 100 sales you agree, 95 of them actually cross the finish line. The national average sits at 70%, and on premium properties where each lost sale costs you upwards of £24,000 in commission, that 25-point gap is worth six figures to your business every single year.
              </p>
            </CardContent>
          </EliteCard>

          {/* Card 2: Secure with a Commitment */}
          <EliteCard
            className="h-full"
            data-testid="elite-card-secure">

            <CardContent className="h-full flex flex-col items-center text-center pt-10 pb-8 px-6">
              <VisualShield />
              <h3 className="mt-6 text-2xl sm:text-[26px] font-didot italic text-white">
                Secure with a Commitment
              </h3>
              <p className="mt-3 text-sm sm:text-[15px] text-white/65 font-manrope leading-relaxed">
                When a sale is agreed, both your buyer and seller sign a Gazeal Elite Reservation Agreement and commit a financial sum that reflects the seriousness of the transaction. If either side walks away without good reason, the committed party is compensated and Gazeal guarantees it. This isn't a handshake or a gentleman's agreement, it's a binding financial commitment that changes how both parties behave from the moment they sign.
              </p>
            </CardContent>
          </EliteCard>

          {/* Card 3: Commitments up to £250K */}
          <EliteCard
            className="h-full"
            data-testid="elite-card-tiers">

            <CardContent className="h-full flex flex-col items-center text-center pt-10 pb-8 px-6">
              <VisualTiers />
              <h3 className="mt-6 text-2xl sm:text-[26px] font-didot italic text-white">
                Commitments up to <span className="bg-gradient-to-r from-[#FFDE59] to-[#FF914D] bg-clip-text text-transparent">£250K</span>
              </h3>
              <p className="mt-3 text-sm sm:text-[15px] text-white/65 font-manrope leading-relaxed">
                A £5,000 commitment on a £3 million property doesn't reflect the seriousness of the people involved or the value of what's at stake. That's exactly why Elite agents asked us to go higher, and now commitment levels run from £10,000 all the way up to £250,000 depending on the property value and what makes sense for your particular transaction. You set the level on every deal, and Gazeal guarantees it so neither party has to put the money down upfront.
              </p>
            </CardContent>
          </EliteCard>

        </div>
      </div>
    </section>);

}
